import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LEAGUES_CONFIG } from "@/components/filters/LeagueFilter";
import { WORLD_CUP_API_LEAGUE_ID, isWorldCupWindow } from "@/lib/worldCup2026";


export type TickerStatus = "live" | "ht" | "ft" | "upcoming";

export interface TickerEditorial {
  type: 'ucl_day' | 'ucl_summary' | 'uel_day' | 'uel_summary' | 'uecl_day' | 'uecl_summary';
  badge: string;
  text: string;
  leagueLogo: string;
}

export interface TickerItem {
  fixture_id: number;
  home_team: string;
  away_team: string;
  home_team_logo: string;
  away_team_logo: string;
  home_score: number | null;
  away_score: number | null;
  status: TickerStatus;
  status_short: string | null;
  elapsed: number | null;
  match_date: string;
  league_short: string;
  // Full game data for drawer
  game: any;
  // Editorial special items (UCL day, final whistle)
  editorial?: TickerEditorial;
}

const SG7_LEAGUE_IDS = LEAGUES_CONFIG.map((l) => l.id);

function mapStatus(statusShort: string | null): TickerStatus {
  if (!statusShort) return "upcoming";
  const s = statusShort.toUpperCase();
  if (s === "HT") return "ht";
  if (["1H", "2H", "ET", "BT", "P", "LIVE"].includes(s)) return "live";
  if (["FT", "AET", "PEN"].includes(s)) return "ft";
  return "upcoming";
}

export interface TickerResult {
  items: TickerItem[];
  fallbackMode: boolean;
  nextFixtureDate: string | null;
}

async function fetchTickerData(): Promise<TickerResult> {
  const now = new Date();
  const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString();
  const twelveHoursAhead = new Date(now.getTime() + 12 * 60 * 60 * 1000).toISOString();

  const gameSelect = `
    id, fixture_id, match_date, status, status_short, home_score, away_score, elapsed,
    home_team:teams!games_home_team_id_fkey(id, name, logo_url, api_football_id),
    away_team:teams!games_away_team_id_fkey(id, name, logo_url, api_football_id),
    league:leagues!games_league_id_fkey(id, name, short_name, api_football_id, logo_url)
  `;

  // Three parallel queries
  const [liveRes, ftRes, upcomingRes] = await Promise.all([
    // LIVE games
    supabase
      .from("games")
      .select(gameSelect)
      .eq("status", "live")
      .order("elapsed", { ascending: false })
      .limit(50),
    // Recent FT games (last 12h)
    supabase
      .from("games")
      .select(gameSelect)
      .eq("status", "finished")
      .gte("match_date", twelveHoursAgo)
      .order("match_date", { ascending: false })
      .limit(20),
    // UPCOMING (next 12h)
    supabase
      .from("games")
      .select(gameSelect)
      .eq("status", "scheduled")
      .gte("match_date", now.toISOString())
      .lte("match_date", twelveHoursAhead)
      .order("match_date", { ascending: true })
      .limit(30),
  ]);

  const wcMode = isWorldCupWindow(now);
  const allowedLeague = (apiId: number | null | undefined) => {
    if (!apiId) return false;
    return wcMode ? apiId === WORLD_CUP_API_LEAGUE_ID : SG7_LEAGUE_IDS.includes(apiId);
  };

  const toItems = (games: any[] | null, fallbackStatus: TickerStatus): TickerItem[] => {
    if (!games) return [];
    return games
      .filter((g) => allowedLeague(g.league?.api_football_id))

      .map((g) => ({
        fixture_id: g.fixture_id,
        home_team: g.home_team?.name || "TBD",
        away_team: g.away_team?.name || "TBD",
        home_team_logo: g.home_team?.logo_url || "",
        away_team_logo: g.away_team?.logo_url || "",
        home_score: g.home_score,
        away_score: g.away_score,
        status: mapStatus(g.status_short) || fallbackStatus,
        status_short: g.status_short,
        elapsed: g.elapsed,
        match_date: g.match_date,
        league_short: g.league?.short_name || "",
        game: g,
      }));
  };

  const liveItems = toItems(liveRes.data, "live");
  const ftItems = toItems(ftRes.data, "ft");
  const upcomingItems = toItems(upcomingRes.data, "upcoming");

  // Dynamic FT capping
  const cappedFt = liveItems.length > 0
    ? ftItems.slice(0, 5)
    : upcomingItems.length > 0
      ? ftItems.slice(0, 8)
      : ftItems;

  const result = [...liveItems, ...cappedFt, ...upcomingItems];

  // Fallback: if base windows returned nothing, expand UPCOMING to 24h
  if (result.length === 0) {
    const twentyFourHoursAhead = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
    const fallbackRes = await supabase
      .from("games")
      .select(gameSelect)
      .eq("status", "scheduled")
      .gte("match_date", now.toISOString())
      .lte("match_date", twentyFourHoursAhead)
      .order("match_date", { ascending: true })
      .limit(30);
    const fallback24Items = toItems(fallbackRes.data, "upcoming");
    if (fallback24Items.length > 0) {
      return { items: fallback24Items, fallbackMode: false, nextFixtureDate: null };
    }

    // No games in 24h — check if next game is >48h away → activate fallback mode
    const { data: nextGame } = await supabase
      .from("games")
      .select("match_date")
      .eq("status", "scheduled")
      .gte("match_date", now.toISOString())
      .order("match_date", { ascending: true })
      .limit(1);

    const nextDate = nextGame?.[0]?.match_date || null;
    const hoursToNext = nextDate
      ? (new Date(nextDate).getTime() - now.getTime()) / (1000 * 60 * 60)
      : Infinity;

    return { items: [], fallbackMode: true, nextFixtureDate: nextDate };
  }

  // --- UEFA Editorial Items (config-driven) ---
  const UEFA_EDITORIAL_CONFIG = [
    {
      apiId: 2, fixtureIdDay: -1, fixtureIdSummary: -2, leagueShort: "UCL",
      badge: "CHAMPIONS LEAGUE DAY", competitionName: "Champions League",
      preMatchVariants: ["Don't miss tonight's action", "Europe's biggest night returns", "Kick-off starts tonight"],
      typeDay: "ucl_day" as const, typeSummary: "ucl_summary" as const, preMatchWindow: 12, postMatchWindow: 8,
    },
    {
      apiId: 3, fixtureIdDay: -3, fixtureIdSummary: -4, leagueShort: "UEL",
      badge: "EUROPA LEAGUE NIGHT", competitionName: "Europa League",
      preMatchVariants: ["Don't miss tonight's action", "Big knockout football tonight", "Kick-off starts tonight"],
      typeDay: "uel_day" as const, typeSummary: "uel_summary" as const, preMatchWindow: 6, postMatchWindow: 6,
    },
    {
      apiId: 848, fixtureIdDay: -5, fixtureIdSummary: -6, leagueShort: "UECL",
      badge: "CONFERENCE LEAGUE NIGHT", competitionName: "Conference League",
      preMatchVariants: ["Don't miss tonight's action", "European football returns tonight", "Kick-off starts tonight"],
      typeDay: "uecl_day" as const, typeSummary: "uecl_summary" as const, preMatchWindow: 6, postMatchWindow: 6,
    },
  ];

  const allItems = [...liveItems, ...ftItems, ...upcomingItems];

  for (const cfg of UEFA_EDITORIAL_CONFIG) {
    const compItems = allItems.filter((i) => i.game?.league?.api_football_id === cfg.apiId);
    if (compItems.length === 0) continue;

    const compLogo = compItems[0].game?.league?.logo_url || "";
    const compLive = compItems.filter((i) => i.status === "live");
    const compUpcoming = compItems.filter((i) => i.status === "upcoming");
    const compFt = compItems.filter((i) => i.status === "ft" || i.status === "ht");

    // Pre-match editorial
    if (compUpcoming.length > 0 && compLive.length === 0) {
      const firstKickoff = new Date(compUpcoming[0].match_date);
      const hoursUntil = (firstKickoff.getTime() - now.getTime()) / (1000 * 60 * 60);
      if (hoursUntil > 0 && hoursUntil <= cfg.preMatchWindow) {
        const variantIdx = Math.floor(now.getHours() / 8) % cfg.preMatchVariants.length;
        result.unshift({
          fixture_id: cfg.fixtureIdDay,
          home_team: "", away_team: "", home_team_logo: "", away_team_logo: "",
          home_score: null, away_score: null,
          status: "upcoming", status_short: null, elapsed: null,
          match_date: compUpcoming[0].match_date,
          league_short: cfg.leagueShort,
          game: null,
          editorial: {
            type: cfg.typeDay,
            badge: cfg.badge,
            text: cfg.preMatchVariants[variantIdx],
            leagueLogo: compLogo,
          },
        });
      }
    }

    // Post-match summary
    if (compLive.length === 0 && compUpcoming.length === 0 && compFt.length > 0) {
      const lastFtDate = new Date(compFt[0].match_date);
      const estimatedEnd = new Date(lastFtDate.getTime() + 2 * 60 * 60 * 1000);
      const hoursSinceEnd = (now.getTime() - estimatedEnd.getTime()) / (1000 * 60 * 60);
      if (hoursSinceEnd <= cfg.postMatchWindow) {
        const totalGoals = compFt.reduce((acc, g) => acc + (g.home_score || 0) + (g.away_score || 0), 0);
        const matchCount = compFt.length;
        const text = matchCount === 1
          ? `${totalGoals} goal${totalGoals !== 1 ? "s" : ""} in tonight's ${cfg.competitionName} fixture`
          : `${totalGoals} goals across tonight's ${matchCount} ${cfg.competitionName} fixtures`;
        result.unshift({
          fixture_id: cfg.fixtureIdSummary,
          home_team: "", away_team: "", home_team_logo: "", away_team_logo: "",
          home_score: null, away_score: null,
          status: "ft", status_short: null, elapsed: null,
          match_date: compFt[0].match_date,
          league_short: cfg.leagueShort,
          game: null,
          editorial: {
            type: cfg.typeSummary,
            badge: "FINAL WHISTLE",
            text,
            leagueLogo: compLogo,
          },
        });
      }
    }
  }

  return { items: result, fallbackMode: false, nextFixtureDate: null };
}

export function useLiveTickerData() {
  return useQuery({
    queryKey: ["live-ticker"],
    queryFn: fetchTickerData,
    refetchInterval: 30_000,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}
